import { formatInr, MONEY_CLASSNAME } from '@/lib/money';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getPortfolio, getPortfolioProfit } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import Navbar from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Landmark, TrendingUp, TrendingDown, Package, ArrowRight, ShieldCheck, Minus, BarChart3, PieChartIcon } from 'lucide-react';

export default function PortfolioPage() {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [portRes, profRes] = await Promise.all([
          getPortfolio(),
          getPortfolioProfit().catch(() => ({ data: null }))
        ]);
        setPortfolio(portRes.data);
        setProfitData(profRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (user) load();
    else setLoading(false);
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 text-center">
          <Landmark className="w-16 h-16 text-primary mx-auto mb-6 opacity-40" />
          <h2 className="font-display text-3xl font-bold mb-4">Your Vault is Locked</h2>
          <p className="text-muted-foreground mb-8">Sign in to view your collection and track your metal value.</p>
          <Link to="/login">
            <Button className="bg-primary text-primary-foreground font-bold uppercase tracking-wide rounded-sm h-12 px-8" data-testid="vault-login-button">
              Sign In <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="portfolio-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-2">
            <Landmark className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">My Vault</h1>
          </div>
          <p className="text-muted-foreground mb-8">Your physical gold & silver collection</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : portfolio && portfolio.items.length > 0 ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <motion.div className="bg-card border border-white/5 p-5 rounded-xl " initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} data-testid="purchase-value">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Purchase Value</p>
                <p className={`${MONEY_CLASSNAME} text-xl font-bold text-foreground`}>{formatInr(portfolio.purchase_value)}</p>
              </motion.div>
              <motion.div className="bg-card border border-white/5 p-5 rounded-xl " initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} data-testid="current-value">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Current Value</p>
                <p className={`${MONEY_CLASSNAME} text-xl font-bold text-gold`}>{formatInr(portfolio.current_value)}</p>
              </motion.div>
              <motion.div 
                className={`p-5 rounded-xl border  ${portfolio.profit_loss >= 0 ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`} 
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} 
                data-testid="profit-loss"
              >
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Profit / Loss</p>
                <div className="flex items-center gap-2">
                  {portfolio.profit_loss > 0 ? (
                    <TrendingUp className="w-4 h-4 text-green-400" />
                  ) : portfolio.profit_loss < 0 ? (
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  ) : (
                    <Minus className="w-4 h-4 text-muted-foreground" />
                  )}
                  <p className={`font-display text-xl font-bold ${portfolio.profit_loss >= 0 ? 'text-green-400' : 'text-red-500'}`}>
                    {portfolio.profit_loss >= 0 ? '+' : '−'}{formatInr(Math.abs(portfolio.profit_loss))}
                  </p>
                </div>
                <p className={`text-xs mt-1 ${portfolio.profit_loss >= 0 ? 'text-green-400/70' : 'text-red-500/70'}`}>
                  {portfolio.profit_loss >= 0 ? '+' : '−'}{portfolio.profit_loss_percent?.toFixed(2)}%
                </p>
              </motion.div>
              <motion.div className="bg-card border border-white/5 p-5 rounded-xl " initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} data-testid="total-items">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Items</p>
                <p className="font-display text-xl font-bold">{portfolio.total_items}</p>
              </motion.div>
              <motion.div className="bg-gold/5 border border-gold/20 p-5 rounded-xl " initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} data-testid="total-gold">
                <p className="text-xs text-gold uppercase tracking-widest mb-2">Gold Weight</p>
                <p className={`${MONEY_CLASSNAME} text-xl font-bold text-gold`}>{portfolio.gold_weight}g</p>
              </motion.div>
              <motion.div className="bg-secondary border border-silver/20 p-5 rounded-xl " initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} data-testid="total-silver">
                <p className="text-xs text-silver uppercase tracking-widest mb-2">Silver Weight</p>
                <p className="font-display text-xl font-bold text-silver">{portfolio.silver_weight}g</p>
              </motion.div>
            </div>

            <Separator className="mb-8" />

            {/* Portfolio Value Chart - Enhanced */}
            {profitData?.summary && (
              <motion.div
                className="bg-card border border-white/5 rounded-xl p-6 mb-8"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                data-testid="portfolio-chart"
              >
                <div className="flex items-center gap-2 mb-6">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <h3 className="font-display font-semibold text-sm uppercase tracking-widest">Portfolio Breakdown</h3>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Pie Chart */}
                  <div className="flex flex-col items-center">
                    <div className="w-full h-48 relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Gold', value: profitData.summary.gold_current_value || 0, color: '#D4AF37' },
                              { name: 'Silver', value: profitData.summary.silver_current_value || 0, color: '#E0E0E0' },
                            ].filter(d => d.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={75}
                            paddingAngle={3}
                            dataKey="value"
                            strokeWidth={0}
                          >
                            {[
                              { name: 'Gold', value: profitData.summary.gold_current_value || 0, color: '#D4AF37' },
                              { name: 'Silver', value: profitData.summary.silver_current_value || 0, color: '#E0E0E0' },
                            ].filter(d => d.value > 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => formatInr(value)}
                            contentStyle={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      {/* Center text */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <p className="text-xs text-muted-foreground">Total</p>
                        <p className={`${MONEY_CLASSNAME} text-lg font-bold text-gold`}>{formatInr(profitData.summary.total_current_value)}</p>
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex items-center justify-center gap-6 mt-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gold" />
                        <span className="text-xs text-muted-foreground">Gold</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-silver" />
                        <span className="text-xs text-muted-foreground">Silver</span>
                      </div>
                    </div>
                  </div>

                  {/* Bar breakdown */}
                  <div>
                    {(() => {
                      const s = profitData.summary;
                      const maxVal = Math.max(s.total_invested, s.total_current_value, 1);
                      const bars = [
                        { label: 'Invested', value: s.total_invested, color: 'bg-muted-foreground/40', textColor: 'text-muted-foreground' },
                        { label: 'Current Value', value: s.total_current_value, color: s.total_profit_loss >= 0 ? 'bg-green-500' : 'bg-red-500', textColor: s.total_profit_loss >= 0 ? 'text-green-400' : 'text-red-400' },
                      ];
                      if (s.gold_invested > 0) {
                        bars.push(
                          { label: 'Gold Invested', value: s.gold_invested, color: 'bg-gold/30', textColor: 'text-gold/70' },
                          { label: 'Gold Value', value: s.gold_current_value, color: 'bg-gold', textColor: 'text-gold' },
                        );
                      }
                      if (s.silver_invested > 0) {
                        bars.push(
                          { label: 'Silver Invested', value: s.silver_invested, color: 'bg-white/20', textColor: 'text-white/50' },
                          { label: 'Silver Value', value: s.silver_current_value, color: 'bg-white/60', textColor: 'text-white/80' },
                        );
                      }
                      return (
                        <div className="space-y-3">
                          {bars.map((bar) => (
                            <div key={bar.label} className="flex items-center gap-3">
                              <span className="text-[11px] text-muted-foreground w-24 flex-shrink-0 text-right truncate">{bar.label}</span>
                              <div className="flex-1 bg-secondary/30 rounded-full h-4 overflow-hidden">
                                <motion.div
                                  className={`h-full rounded-full ${bar.color}`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.max(2, (bar.value / maxVal) * 100)}%` }}
                                  transition={{ duration: 0.8, ease: 'easeOut' }}
                                />
                              </div>
                              <span className={`text-xs font-mono w-24 flex-shrink-0 ${bar.textColor}`}>{formatInr(bar.value)}</span>
                            </div>
                          ))}
                          
                          {/* P&L Summary */}
                          <div className="flex justify-between pt-4 mt-2 border-t border-white/5">
                            <span className="text-sm text-muted-foreground">Total P&L</span>
                            <div className="text-right">
                              <span className={`font-mono font-bold text-lg ${s.total_profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {s.total_profit_loss >= 0 ? '+' : '−'}{formatInr(Math.abs(s.total_profit_loss))}
                              </span>
                              <span className={`ml-2 text-sm ${s.total_profit_loss >= 0 ? 'text-green-400/70' : 'text-red-400/70'}`}>
                                ({s.total_profit_loss_percent?.toFixed(1)}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Items */}
            <div className="space-y-4">
              {portfolio.items.map((item, i) => (
                <motion.div
                  key={item.transaction_id}
                  className="bg-card border border-white/5 hover:border-gold/30 rounded-xl p-4 transition-colors duration-300 "
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                  data-testid={`portfolio-item-${item.transaction_id}`}
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={item.image_url}
                      alt={item.collectible_name}
                      className="w-20 h-20 object-cover rounded-sm flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className={`text-xs rounded-sm ${item.metal_type === 'gold' ? 'border-primary/50 text-primary' : 'border-silver/50 text-silver'}`}>
                          {item.metal_type.toUpperCase()} {item.weight}g
                        </Badge>
                        <Badge variant="outline" className="text-xs rounded-sm border-green-500/30 text-green-400">
                          <ShieldCheck className="w-3 h-3 mr-1" /> Verified
                        </Badge>
                      </div>
                      <h3 className="font-display font-bold truncate">{item.collectible_name}</h3>
                      <p className="text-xs text-muted-foreground">
                        Purchased {new Date(item.purchase_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-muted-foreground">Paid</p>
                      <p className="font-mono text-sm">{formatInr(item.purchase_price)}</p>
                      <p className="text-xs text-muted-foreground mt-2">Current Value</p>
                      <p className="font-mono text-sm text-gold">{formatInr(item.current_metal_value)}</p>
                      <p className={`text-xs mt-1 font-medium ${item.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {item.profit_loss >= 0 ? '+' : '−'}{formatInr(Math.abs(item.profit_loss))} ({item.profit_loss >= 0 ? '+' : '−'}{item.profit_loss_percent?.toFixed(1)}%)
                      </p>
                      {item.certificate_id && (
                        <Link to={`/certificate/${item.certificate_id}`} className="text-[11px] text-gold hover:underline mt-2 inline-block" data-testid={`cert-link-${item.transaction_id}`}>
                          View Certificate
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="font-display text-2xl font-bold mb-2">Empty Vault</h3>
            <p className="text-muted-foreground mb-6">You haven't purchased any collectibles yet.</p>
            <Link to="/">
              <Button className="bg-primary text-primary-foreground font-bold uppercase tracking-wide rounded-sm h-12 px-8" data-testid="browse-drops-button">
                Browse Drops <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
